/**
 * Patches `supabase.auth.getUser()` to read from the cached session in
 * local storage instead of making a network round-trip to `/auth/v1/user`
 * on every call.
 *
 * Why: the codebase has ~105 call sites of `supabase.auth.getUser()`.
 * After login, dozens of components mount in parallel and each one fires
 * its own request, which the auth server serializes — producing the
 * 5–15 s post-login stall and the auth-log flood (~30 `/user` hits in a
 * single second).
 *
 * `supabase.auth.getSession()` reads the signed JWT from local storage
 * (no network) and only refreshes it when the token is near expiry. The
 * embedded `session.user` is the same identity we need everywhere
 * `getUser()` was previously used to "who am I right now?".
 *
 * This patch preserves the original `{ data: { user }, error }` return
 * shape, so all existing destructuring code (`const { data: { user } } =
 * await supabase.auth.getUser();`) keeps working untouched.
 *
 * Side note: this also makes the browser tab usable offline after login,
 * since identifying the current user no longer requires network access.
 */
import { supabase } from "@/integrations/supabase/client";

let patched = false;

export function patchSupabaseGetUser() {
  if (patched) return;
  patched = true;

  const auth = supabase.auth as any;
  const original = auth.getUser.bind(auth);

  auth.getUser = async (jwt?: string) => {
    // If a specific JWT was passed in, fall back to the original
    // implementation — that variant is used to validate arbitrary tokens
    // and MUST hit the server.
    if (jwt) return original(jwt);

    try {
      const { data, error } = await auth.getSession();
      if (error) {
        return { data: { user: null }, error };
      }
      return { data: { user: data?.session?.user ?? null }, error: null };
    } catch (error) {
      return { data: { user: null }, error: error as Error };
    }
  };
}
